import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import MessageModal from '../components/MessageModal';

// Fonction pour générer un ID de transaction unique
const generateTransactionId = () => {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0].replace(/-/g, '');
  const random = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
  return `TX-${dateStr}-${random}`;
};

const TransactionForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    type: '', // 'buy' or 'sell'
    amount: '',
    currency: 'USD',
    location: '', // 'bamako' or 'dubai'
    partner: '',
    goldPriceExecutable: '',
    weightGross: '', // Poids Air (poids brut) - SEUL CHAMP OBLIGATOIRE pour calculs
    weightWater: '', // Poids dans l'eau - OBLIGATOIRE pour calcul densité
    pricePerGramCfa: '', // Prix du gramme en FCFA - OBLIGATOIRE pour calcul montant
    exchangeRateFcfa: '',
    stopLoss: '',
    takeProfit: '',
    remarks: '',
    date: new Date().toISOString().split('T')[0]
  });

  const [currentGoldPrice, setCurrentGoldPrice] = useState(null); // Prix XAUUSD actuel

  const [transactionId] = useState(generateTransactionId());
  const [modal, setModal] = useState({ show: false, type: 'info', title: '', message: '' });

  // Charger le prix XAUUSD actuel au montage du composant
  React.useEffect(() => {
    const loadGoldPrice = async () => {
      try {
        const goldPriceService = (await import('../services/goldPrice')).default;
        const price = await goldPriceService.fetchGoldPrice();
        setCurrentGoldPrice(price);
      } catch (error) {
        console.warn('Impossible de charger le prix XAUUSD:', error);
      }
    };
    loadGoldPrice();
  }, []);

  // Calcul automatique de la densité (Module Or)
  const calculateDensity = () => {
    const weightAir = parseFloat(formData.weightGross) || 0;
    const weightWater = parseFloat(formData.weightWater) || 0;
    
    if (weightAir > 0 && weightWater > 0 && weightAir > weightWater) {
      const density = weightAir / (weightAir - weightWater);
      return parseFloat(density.toFixed(4));
    }
    return null;
  };

  // Table de correspondance densité → carat (simplifiée côté client, le serveur fait le vrai calcul)
  const getCaratFromDensity = (density) => {
    if (!density) return null;
    if (density >= 17.0 && density < 18.0) return 21.0;
    if (density >= 18.0 && density < 18.5) return 22.0;
    if (density >= 18.5 && density < 19.3) return 23.0;
    if (density >= 19.3) return 24.0;
    return null;
  };

  // Calcul du poids pur (automatique depuis carat calculé)
  const calculatePureWeight = () => {
    const weightGross = parseFloat(formData.weightGross) || 0;
    const carat = getCaratFromDensity(calculateDensity()); // Utilise uniquement le carat calculé
    
    if (weightGross > 0 && carat) {
      const pureWeight = weightGross * (carat / 24);
      return parseFloat(pureWeight.toFixed(3));
    }
    return null;
  };

  // Calcul du montant total FCFA
  const calculateTotalAmount = () => {
    const weightPure = calculatePureWeight();
    const pricePerGram = parseFloat(formData.pricePerGramCfa) || 0;
    
    if (weightPure > 0 && pricePerGram > 0) {
      const totalAmount = weightPure * pricePerGram;
      return parseFloat(totalAmount.toFixed(2));
    }
    return null;
  };

  // Calcul de la valeur USD internationale (poids pur → onces → XAUUSD)
  const calculateValueUSD = () => {
    const weightPure = calculatePureWeight();
    const goldPriceXAUUSD = currentGoldPrice || parseFloat(formData.goldPriceExecutable) || 0;
    
    if (weightPure > 0 && goldPriceXAUUSD > 0) {
      // Conversion grammes → onces (1 once = 31.1035 g)
      const ounces = weightPure / 31.1035;
      // Valeur USD = onces × prix XAUUSD
      const valueUSD = ounces * goldPriceXAUUSD;
      return parseFloat(valueUSD.toFixed(2));
    }
    return null;
  };

  // Calculs automatiques
  const density = calculateDensity();
  const caratCalculated = getCaratFromDensity(density);
  const purityEstimated = caratCalculated != null ? Math.round((caratCalculated / 24) * 10000) / 100 : null;
  const weightPure = calculatePureWeight();
  const totalAmountCalculated = calculateTotalAmount();
  const valueUSDCalculated = calculateValueUSD();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.type === 'buy' && (!formData.weightGross || !formData.weightWater || !formData.pricePerGramCfa) && !formData.amount) {
      setModal({
        show: true,
        type: 'error',
        title: 'Saisie incomplète',
        message: 'Pour un achat : renseignez poids air, poids eau et prix au gramme (FCFA) pour les calculs automatiques, ou bien un montant principal ci-dessus.'
      });
      return;
    }
    try {
      const api = (await import('../services/api')).default;
      const database = (await import('../services/database')).default;
      const goldPriceService = (await import('../services/goldPrice')).default;
      
      // Récupérer le prix indicatif actuel depuis l'API (temps réel)
      const indicativePrice = await goldPriceService.fetchGoldPrice();
      
      // Récupérer l'utilisateur connecté
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Préparer les données de la transaction (CDC: valeur = poids fin × prix du gramme)
      const amountForApi = formData.amount || valueUSDCalculated || totalAmountCalculated || 0;
      const transactionData = {
        ...formData,
        amount: amountForApi,
        currency: formData.currency || 'USD',
        transaction_id: transactionId,
        gold_price_indicative: indicativePrice,
        gold_price_executable: formData.goldPriceExecutable || indicativePrice || null,
        created_by: user.id || 1,
        partner_id: formData.partner || null,
        // Carat calculé automatiquement (pas de saisie manuelle)
        carat_gross: caratCalculated || null,
        carat_net: caratCalculated || null,
        weight_gross: formData.weightGross || null,
        weight_net: weightPure || null, // Poids net = poids pur calculé
        weight_water: formData.weightWater || null,
        price_per_gram_cfa: formData.pricePerGramCfa || null,
        total_amount_calculated: totalAmountCalculated || null,
        // Valeur USD calculée automatiquement
        value_usd_calculated: valueUSDCalculated || null,
        exchange_rate_fcfa: formData.exchangeRateFcfa || null,
        stop_loss: formData.stopLoss || null,
        take_profit: formData.takeProfit || null,
        remarks: formData.remarks || null,
        synced: 0 // Non synchronisé par défaut
      };
      
      // 1. Sauvegarder D'ABORD dans la base de données locale (mode offline)
      try {
        await database.createTransaction(transactionData);
        console.log('✅ Transaction sauvegardée localement (mode offline)');
      } catch (dbError) {
        console.error('Erreur sauvegarde locale:', dbError);
        // Continuer quand même, on essaiera de sauvegarder sur le serveur
      }
      
      // 2. Essayer de synchroniser avec le serveur (si connecté)
      if (navigator.onLine) {
        try {
          const response = await api.post('/transactions', transactionData);
          
          if (response.data) {
            // Marquer comme synchronisé dans la base locale
            try {
              const localTx = await database.getTransactions({ 
                searchTerm: transactionId, 
                searchType: 'transactionId' 
              });
              if (localTx && localTx.length > 0) {
                await database.updateTransaction(localTx[0].id, { synced: 1 });
              }
            } catch (syncError) {
              console.warn('Erreur marquage synchronisé:', syncError);
            }
            
            setModal({
              show: true,
              type: 'success',
              title: 'Succès',
              message: t('transactionForm.success')
            });
            const dossierId = response.data?.transaction?.id || response.data?.transaction?.transaction_id;
            setModal({ show: false, type: 'info', title: '', message: '' });
            if (dossierId) {
              navigate(`/transactions/${dossierId}/dossier`);
            } else {
              navigate('/transactions');
            }
            return;
          }
        } catch (apiError) {
          console.warn('⚠️ Serveur non disponible, transaction sauvegardée localement uniquement');
          // La transaction est déjà sauvegardée localement, on continue
        }
      } else {
        console.log('📴 Mode offline: transaction sauvegardée localement uniquement');
      }
      
      // Si on arrive ici, la transaction est sauvegardée localement
      // Elle sera synchronisée automatiquement quand internet reviendra
      setModal({
        show: true,
        type: 'success',
        title: 'Succès',
        message: t('transactionForm.success') + ' (sauvegardée localement, synchronisation automatique à la reconnexion)'
      });
      setTimeout(() => {
        setModal({ show: false, type: 'info', title: '', message: '' });
        navigate('/transactions');
      }, 3000);
      
    } catch (error) {
      console.error('Erreur enregistrement transaction:', error);
      setModal({
        show: true,
        type: 'error',
        title: 'Erreur',
        message: error.response?.data?.message || t('transactionForm.error')
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">{t('transactionForm.title')}</h1>
        
        {/* Numéro de Transaction - Visible mais discret - Responsive */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="text-xs sm:text-sm text-gray-500 mb-1">{t('transactionForm.txNumber')}</div>
          <div className="text-lg sm:text-xl md:text-2xl font-bold text-gray-700 break-all">{transactionId}</div>
        </div>

        <div className="card-modern">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Type de Transaction - TRÈS GRANDS BOUTONS selon CDC - Responsive */}
            <div>
              <label className="block text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 text-center">{t('transactionForm.type')}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'buy' }))}
                  className={`py-6 sm:py-8 px-4 sm:px-6 rounded-xl sm:rounded-2xl text-2xl sm:text-3xl md:text-4xl font-black transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 sm:gap-4 ${
                    formData.type === 'buy'
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-2xl'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300 shadow-lg'
                  }`}
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {t('transactionForm.buy')}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'sell' }))}
                  className={`py-6 sm:py-8 px-4 sm:px-6 rounded-xl sm:rounded-2xl text-2xl sm:text-3xl md:text-4xl font-black transition-all duration-200 transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 sm:gap-4 ${
                    formData.type === 'sell'
                      ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-2xl'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300 shadow-lg'
                  }`}
                >
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {t('transactionForm.sell')}
                </button>
              </div>
            </div>

            {/* Montant - GRAND INPUT selon CDC - Responsive */}
            <div>
              <label className="block text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">{t('transactionForm.amount')}</label>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  className="input-large flex-1 text-xl sm:text-2xl md:text-3xl font-bold"
                  required
                  autoFocus
                />
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="input-large text-lg sm:text-xl md:text-2xl font-bold w-full sm:w-32"
                >
                  <option value="USD">USD</option>
                  <option value="AED">AED</option>
                  <option value="FCFA">FCFA</option>
                </select>
              </div>
              {formData.currency === 'USD' && formData.amount && (
                <div className="mt-3 text-lg text-gray-600 font-semibold">
                  = {(parseFloat(formData.amount) * 3.67).toFixed(2)} AED
                </div>
              )}
              {formData.currency === 'AED' && formData.amount && (
                <div className="mt-3 text-lg text-gray-600 font-semibold">
                  = {(parseFloat(formData.amount) / 3.67).toFixed(2)} USD
                </div>
              )}
              {formData.currency === 'FCFA' && (
                <div className="mt-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Taux USD/FCFA (manuel) *
                  </label>
                  <input
                    type="number"
                    name="exchangeRateFcfa"
                    value={formData.exchangeRateFcfa}
                    onChange={handleChange}
                    placeholder="Ex: 600"
                    step="0.01"
                    className="input-large"
                    required
                  />
                </div>
              )}
            </div>

            {/* Comptoir - GRANDS BOUTONS selon CDC - Responsive */}
            <div>
              <label className="block text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">{t('transactionForm.counter')}</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, location: 'bamako' }))}
                  className={`py-5 sm:py-6 px-4 sm:px-6 rounded-xl sm:rounded-2xl text-xl sm:text-2xl md:text-3xl font-black transition-all duration-200 transform hover:scale-105 active:scale-95 ${
                    formData.location === 'bamako'
                      ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-2xl'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300 shadow-lg'
                  }`}
                >
                  {t('transactionForm.bamako')}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, location: 'dubai' }))}
                  className={`py-5 sm:py-6 px-4 sm:px-6 rounded-xl sm:rounded-2xl text-xl sm:text-2xl md:text-3xl font-black transition-all duration-200 transform hover:scale-105 active:scale-95 ${
                    formData.location === 'dubai'
                      ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-2xl'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300 shadow-lg'
                  }`}
                >
                  {t('transactionForm.dubai')}
                </button>
              </div>
            </div>

            {/* Section Module Or - Calculs automatiques (repliable) */}
            <details className="bg-gray-50 rounded-xl p-6" open>
              <summary className="text-xl font-bold text-gray-700 cursor-pointer mb-4 flex items-center gap-3">
                <svg className="w-6 h-6 text-gold-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Module Or - Calculs automatiques
              </summary>
              <div className="space-y-6 mt-4">
                {/* Champs de saisie obligatoires */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
                  <h5 className="text-lg font-bold text-gray-800 mb-3">Saisie rapide (recommandée pour le calcul auto)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-lg font-semibold text-gray-700 mb-2">
                        Poids Air (g) *
                      </label>
                      <input
                        type="number"
                        name="weightGross"
                        value={formData.weightGross}
                        onChange={handleChange}
                        placeholder="Ex: 665.17"
                        step="0.001"
                        className="input-large"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Poids brut de l'or dans l'air
                      </div>
                    </div>
                    <div>
                      <label className="block text-lg font-semibold text-gray-700 mb-2">
                        Poids Eau (g) *
                      </label>
                      <input
                        type="number"
                        name="weightWater"
                        value={formData.weightWater}
                        onChange={handleChange}
                        placeholder="Ex: 39.83"
                        step="0.001"
                        className="input-large"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Poids de l'or mesuré dans l'eau
                      </div>
                    </div>
                    <div>
                      <label className="block text-lg font-semibold text-gray-700 mb-2">
                        Prix du gramme (FCFA) *
                      </label>
                      <input
                        type="number"
                        name="pricePerGramCfa"
                        value={formData.pricePerGramCfa}
                        onChange={handleChange}
                        placeholder="Ex: 89400"
                        step="0.01"
                        className="input-large"
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Prix de base du gramme d'or en FCFA
                      </div>
                    </div>
                  </div>
                </div>

                {/* Calculs automatiques — affichage progressif (temps réel) */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 mt-4">
                  <h4 className="text-xl font-bold text-gray-800 mb-4">Indicateurs (mis à jour au fil de la saisie)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    <div className={`p-4 rounded-lg shadow-sm border-l-4 ${formData.weightGross && formData.weightWater ? 'bg-white border-purple-500' : 'bg-gray-100 border-gray-300 opacity-70'}`}>
                      <div className="text-sm text-gray-600 mb-1">Densité</div>
                      <div className="text-3xl font-bold text-purple-600">
                        {density !== null ? density.toFixed(4) : '—'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Remplir poids air + eau</div>
                    </div>
                    <div className={`p-4 rounded-lg shadow-sm border-l-4 ${density !== null ? 'bg-white border-indigo-500' : 'bg-gray-100 border-gray-300 opacity-70'}`}>
                      <div className="text-sm text-gray-600 mb-1">Carat (K) / Pureté (%)</div>
                      <div className="text-3xl font-bold text-indigo-600">
                        {caratCalculated !== null ? `${caratCalculated}K` : '—'}
                        {purityEstimated != null && (
                          <span className="block text-lg font-semibold text-gray-700 mt-1">{purityEstimated} %</span>
                        )}
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg shadow-sm border-l-4 ${weightPure !== null ? 'bg-white border-green-500' : 'bg-gray-100 border-gray-300 opacity-70'}`}>
                      <div className="text-sm text-gray-600 mb-1">Poids pur (g)</div>
                      <div className="text-3xl font-bold text-green-600">
                        {weightPure !== null ? weightPure.toFixed(3) : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className={`p-6 rounded-lg shadow-lg text-white ${totalAmountCalculated !== null ? 'bg-gradient-to-r from-blue-500 to-blue-600' : 'bg-gray-400'}`}>
                      <div className="text-sm font-semibold mb-2 opacity-90">Montant FCFA (fournisseur)</div>
                      <div className="text-4xl font-black mb-2">
                        {totalAmountCalculated !== null ? totalAmountCalculated.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                      </div>
                      <div className="text-xs opacity-90">Besoin : poids pur + prix / g FCFA</div>
                    </div>
                    <div className={`p-6 rounded-lg shadow-lg text-white ${valueUSDCalculated !== null ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : 'bg-gray-400'}`}>
                      <div className="text-sm font-semibold mb-2 opacity-90">Valeur USD (XAUUSD)</div>
                      <div className="text-4xl font-black mb-2">
                        {valueUSDCalculated !== null ? valueUSDCalculated.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
                      </div>
                      <div className="text-xs opacity-90">
                        Besoin : poids pur + cours (spot ou exécutable)
                        {currentGoldPrice != null && <div className="mt-1">Spot: ${Number(currentGoldPrice).toFixed(2)}/oz</div>}
                      </div>
                    </div>
                  </div>
                  {valueUSDCalculated != null && weightPure != null && (
                    <div className="mt-4 pt-4 border-t border-purple-300 text-sm text-gray-700">
                      <span className="font-semibold">Détail USD :</span>{' '}
                      {weightPure.toFixed(3)} g → {(weightPure / 31.1035).toFixed(4)} oz
                      {currentGoldPrice != null && ` @ $${Number(currentGoldPrice).toFixed(2)}/oz`}
                    </div>
                  )}
                </div>

                {/* Partenaire (optionnel) */}
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">{t('transactionForm.goldDetails.partner')}</label>
                  <input
                    type="text"
                    name="partner"
                    value={formData.partner}
                    onChange={handleChange}
                    placeholder={t('transactionForm.goldDetails.partnerPlaceholder')}
                    className="input-large"
                  />
                </div>

                {/* Gestion du risque + remarks */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-2">
                      Stop Loss (USD/oz)
                    </label>
                    <input
                      type="number"
                      name="stopLoss"
                      value={formData.stopLoss}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="Ex: 2400"
                      className="input-large"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-semibold text-gray-700 mb-2">
                      Take Profit (USD/oz)
                    </label>
                    <input
                      type="number"
                      name="takeProfit"
                      value={formData.takeProfit}
                      onChange={handleChange}
                      step="0.01"
                      placeholder="Ex: 2800"
                      className="input-large"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-lg font-semibold text-gray-700 mb-2">
                    Remarks
                  </label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    rows={3}
                    className="input-large"
                    placeholder="Commentaire libre sur la transaction"
                  />
                </div>
              </div>
            </details>

            {/* Date (cachée, automatique) */}
            <input
              type="hidden"
              name="date"
              value={formData.date}
            />

            {/* Bouton Enregistrer - TRÈS GRAND selon CDC - Responsive */}
            <button
              type="submit"
              className="w-full btn-gold text-lg sm:text-xl md:text-2xl lg:text-3xl py-5 sm:py-6 md:py-8 flex items-center justify-center gap-2 sm:gap-3"
            >
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('transactionForm.submit')}
            </button>
          </form>
        </div>
      </div>
      <MessageModal
        show={modal.show}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal({ show: false, type: 'info', title: '', message: '' })}
      />
    </div>
  );
};

export default TransactionForm;
